/**
 * Der Vorrat der Reflex-Antwort — die EINZIGEN Sätze, die eine Reflex-Antwort je sprechen dürfte. (17.10.2026)
 *
 * Bis 17.10.2026 stand der Vorrat in server/reflex-antwort.ts. Jetzt hier, weil ihn ZWEI lesen: der Server (Weiche,
 * Schatten-Zeile) und die Hörprobe in den Einstellungen (frontend/lib/reflex-hoerprobe-karte.ts). Ein Vorrat, zwei
 * Leser — keine Kopie. server/reflex-antwort.ts reicht alles unverändert weiter.
 *
 * Server-Text, in Vesperas Ton (du-Form, ruhig, kein Kundendienst-Ton — dieselben Regeln wie der Pool der Vorantwort),
 * ohne Namen, Zahl, Ergebnis oder Zusage einer Tat, ohne Frage (eine Frage öffnete eine Rückfrage und hielte das Mikro
 * offen). Jeder Satz ist wahr, WEIL der Server die Rede erkannt hat und läuft („Ja, ich höre dich.“).
 *
 * ⚠️ Vorläufig: Ob sie nach Vespera klingen, entscheidet Martins blinde Hörprobe, nicht dieser Text.
 * ⚠️ KENNUNG = Art + Platz („dank-3“). Wer einen Satz ÄNDERT, ändert seine Prüfzahl — eine alte Bewertung gilt dann
 *    nicht mehr (`satzPruefzahl`). Wer einen Satz aus der Mitte NIMMT, verschiebt die Plätze dahinter: dann die
 *    Bewertungen dieser Art neu hören. Durchgefallene Sätze werden NICHT automatisch entfernt (Martin entscheidet).
 */

export const REFLEX_ARTEN = ["dank", "gruss", "abschied", "hoerprobe"] as const;
export type ReflexArt = (typeof REFLEX_ARTEN)[number];

export const REFLEX_VORRAT: Readonly<Record<ReflexArt, readonly string[]>> = {
  dank: ["Passt.", "Alles gut.", "Immer wieder.", "Dafür bin ich da.", "Klar, jederzeit."],
  gruss: ["Hallo, ich bin da.", "Hallo, ich höre dich.", "Da bin ich.", "Hallo, ich bin bereit."],
  abschied: ["Bis später.", "Bis bald.", "Mach's gut.", "Bis dann.", "Bis zum nächsten Mal."],
  hoerprobe: ["Ja, ich höre dich.", "Ich höre dich.", "Ich bin da und höre dich.", "Ja, ich höre zu."],
};

/** Wie die Arten in der Auswertung der Hörprobe heißen — erst NACH der Bewertung gezeigt (blind). */
export const REFLEX_ART_NAME: Readonly<Record<ReflexArt, string>> = {
  dank: "Dank",
  gruss: "Gruß",
  abschied: "Abschied",
  hoerprobe: "Hörprobe",
};

export interface ReflexSatz {
  /** „dank-3“: Art und Platz im Vorrat (ab 1) — dieselbe Nummer wie `satz=` in der Zeile `[reflexantwort]`. */
  kennung: string;
  art: ReflexArt;
  nr: number;
  satz: string;
  /** Prüfzahl des Wortlauts: ändert sich der Satz, gilt eine alte Bewertung nicht mehr. */
  pruefzahl: number;
}

/**
 * Prüfzahl eines Satzes (FNV-1a, 32 Bit, ohne Vorzeichen). Keine Sicherheit — nur „ist das noch derselbe Wortlaut?“.
 * Rein, im Browser und am Server dieselbe Zahl.
 */
export function satzPruefzahl(satz: string): number {
  let h = 0x811c9dc5;
  for (const ch of satz.normalize("NFC")) {
    const c = ch.codePointAt(0) ?? 0;
    h ^= c;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** Alle Sätze des Vorrats in fester Reihenfolge (Art, dann Platz). */
export function reflexSaetze(): ReflexSatz[] {
  return REFLEX_ARTEN.flatMap((art) =>
    REFLEX_VORRAT[art].map((satz, i) => ({ kennung: `${art}-${i + 1}`, art, nr: i + 1, satz, pruefzahl: satzPruefzahl(satz) })),
  );
}

/** Der Satz zu einer Kennung — oder null (unbekannte Kennungen werden nie gespeichert). */
export function reflexSatzFuer(kennung: unknown): ReflexSatz | null {
  if (typeof kennung !== "string" || kennung.length > 32) return null;
  return reflexSaetze().find((s) => s.kennung === kennung) ?? null;
}
